import { isHidden } from '@amazon/vinyl-tsx'
import { createDisposer, globalRef, type Unsubscribe } from '@amazon/vinyl-util'
import { documentEvents, tabbableQuery } from '@/util/interaction'

export interface DialogFocusOptions {
    /** Called for Escape; the dialog decides how (and whether) to close. */
    readonly close: () => void
    /** Initial focus; defaults to `[autofocus]`, then the first control not marked `data-dialog-dismiss`. */
    readonly initialFocus?: HTMLElement | null
}

const openDialogs = globalRef<{ readonly stack: object[] }>(() => ({ stack: [] }))

/** A key or focus aimed at a menu opened from the dialog (mounted outside it) is the menu's to handle. */
function inOwnMenu(dialog: HTMLElement, target: EventTarget | null): boolean {
    return (
        target instanceof Element && !dialog.contains(target) && !!target.closest('[role="menu"]')
    )
}

/** A radio group is one Tab stop: its checked radio, else its first. */
function isRadioTabStop(radio: HTMLInputElement, all: readonly HTMLElement[]): boolean {
    if (radio.checked || !radio.name) return true
    const group = all.filter(
        (el): el is HTMLInputElement =>
            el instanceof HTMLInputElement && el.type === 'radio' && el.name === radio.name
    )
    return !group.some((el) => el.checked) && group[0] === radio
}

/** `dialog`'s Tab stops in document order, skipping hidden controls. */
export function dialogTabStops(dialog: HTMLElement): HTMLElement[] {
    const all = Array.from(dialog.querySelectorAll<HTMLElement>(tabbableQuery)).filter(
        (el) => !isHidden(el)
    )
    return all.filter(
        (el) => !(el instanceof HTMLInputElement && el.type === 'radio') || isRadioTabStop(el, all)
    )
}

/**
 * Modal focus for an open dialog: focus moves inside, Tab / Shift+Tab cycle
 * its controls, focus that strays outside is pulled back, and Escape calls
 * `close` (preventDefaulted, so app Back and TV spatial nav skip it). Bind on
 * open; the returned disposer, called on close, hands focus back to the opener.
 * Listeners sit at the document capture phase so the dialog wins Escape over
 * page-level shells (settings, chat history) that navigate on it. With dialogs
 * stacked, only the most recently opened one handles keys and focus.
 */
export function trapDialogFocus(dialog: HTMLElement, options: DialogFocusOptions): Unsubscribe {
    const { add, dispose } = createDisposer()
    const opener = document.activeElement as HTMLElement | null
    const token = {}
    const { stack } = openDialogs.value
    stack.push(token)
    add(() => {
        const index = stack.indexOf(token)
        if (index !== -1) stack.splice(index, 1)
    })
    const owns = (event: Event): boolean =>
        stack.at(-1) === token && !inOwnMenu(dialog, event.target)

    const firstStop = (): HTMLElement | undefined => dialogTabStops(dialog)[0]
    const stops = dialogTabStops(dialog)
    const initial =
        options.initialFocus ??
        stops.find((el) => el.autofocus || el.hasAttribute('autofocus')) ??
        stops.find((el) => !el.hasAttribute('data-dialog-dismiss')) ??
        stops.at(0) ??
        dialog
    initial.focus()

    add(
        documentEvents.on(
            'keydown',
            (event) => {
                if (!owns(event)) return
                if (event.key === 'Escape') {
                    event.preventDefault()
                    event.stopPropagation()
                    options.close()
                    return
                }
                if (event.key !== 'Tab') return
                event.preventDefault()
                const stops = dialogTabStops(dialog)
                if (stops.length === 0) {
                    dialog.focus()
                    return
                }
                const current = stops.indexOf(document.activeElement as HTMLElement)
                const last = stops.length - 1
                const next = event.shiftKey
                    ? current <= 0
                        ? last
                        : current - 1
                    : current === last
                      ? 0
                      : current + 1
                stops[next].focus()
            },
            { capture: true }
        )
    )

    add(
        documentEvents.on(
            'focus',
            (event) => {
                if (!owns(event) || dialog.contains(event.target as Node)) return
                event.stopPropagation()
                event.preventDefault()
                firstStop()?.focus()
            },
            { capture: true }
        )
    )

    add(() => {
        if (opener && document.contains(opener) && typeof opener.focus === 'function') {
            opener.focus()
        }
    })

    return dispose
}
